// PRC-048 (bocetos S2 y S3, diseño 33 apartado 6): panel «Gestión del aviso».
// El mismo panel en el Overview, la página de Cadencia y la ficha de la
// relación. Registrar es elegir: opciones pulsables, texto libre opcional y
// conclusión generada y editable. El panel no escribe: devuelve la decisión y
// quien lo abre la guarda con «Deshacer» (useDeferredCadenceActions).
import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Drawer, IconButton, InputBase } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useQuery } from '@tanstack/react-query';
import ProaDatePicker from '../../../../components/Common/ProaDatePicker';
import { J } from '../../../pricingJourney/journeyTokens';
import { formatEuro, formatInteger, formatPercent } from '../../../pricingJourney/formatNumber';
import {
  getMarginCenterRelationshipActivity12m,
  getMarginCenterSubstituteProducts,
} from '../../services/marginCenterService';
import type { CadenceOpenFollowUp, SubstituteProductItem } from '../../types/cadenceAttention';
import {
  ACTIONS_TAKEN,
  CAUSE_REASONS,
  NEXT_ACTIONS,
  OTHER_ACTION,
  RESOLUTIONS,
  REVIEW_SHORTCUTS,
  SUBSTITUTION_REASON,
  buildCauseConclusion,
  buildOpeningReason,
  margin12mLabel,
  sales12mValue,
  formatDayDate,
  reviewDateFor,
  reviewDateToIso,
  toDateOnly,
  usualFrequencyLabel,
  type CadenceResolutionChoice,
  type ReviewShortcut,
} from './cadenceManagementTexts';

export interface CadenceManagementTarget {
  customer_id: string;
  customer_label: string;
  product_id: string;
  product_label: string;
  /** Episodio del aviso; null = relación sin aviso activo (solo «En gestión comercial», origen Manual). */
  first_outside_p90_date: string | null;
  last_purchase_date: string | null;
  days_since_last_purchase: number | null;
  p90_interval_days: number | null;
}

export type CadenceDecision =
  | { kind: 'cause'; reason: string; substituteProductId: string | null; conclusion: string }
  | { kind: 'commercial'; openingReason: string; nextAction: string; nextReviewIso: string; followUpId: string | null }
  | { kind: 'not_relevant'; note: string };

interface Props {
  open: boolean;
  target: CadenceManagementTarget | null;
  onClose: () => void;
  onConfirm: (target: CadenceManagementTarget, decision: CadenceDecision) => void;
}

const lblSx = { fontSize: 12, fontWeight: 700, color: J.body, m: '14px 0 6px', fontFamily: J.font } as const;
const optSx = { fontStyle: 'normal', fontWeight: 500, color: '#64748b' } as const;
const noteSx = { fontSize: 12, color: J.muted, fontFamily: J.font } as const;
const inputSx = {
  width: '100%', border: '1px solid #334155', borderRadius: '10px', px: '12px', py: '7px',
  fontSize: 13.5, color: J.text, bgcolor: 'rgba(2,6,23,.5)', fontFamily: J.font,
  '&.Mui-focused': { borderColor: J.sky },
} as const;

const Chip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={on}
    sx={{
      border: '1px solid', borderColor: on ? J.sky : '#334155', borderRadius: 999, px: '12px', py: '5px',
      fontSize: 12.5, fontWeight: 600, fontFamily: J.font, color: on ? J.skyText : J.body,
      bgcolor: on ? 'rgba(56,189,248,.14)' : 'transparent',
      '&:hover': { borderColor: on ? J.sky : '#475569' },
    }}
  >
    {children}
  </ButtonBase>
);

const FootButton: React.FC<{ primary?: boolean; disabled?: boolean; onClick?: () => void; type?: 'button' | 'submit'; children: React.ReactNode }> = ({ primary, disabled, onClick, type = 'button', children }) => (
  <ButtonBase
    type={type}
    disabled={disabled}
    onClick={onClick}
    sx={{
      height: 34, px: '14px', borderRadius: '10px', fontSize: 13, fontWeight: 700, fontFamily: J.font,
      border: '1px solid', borderColor: primary ? J.sky : '#334155',
      bgcolor: primary ? J.sky : 'transparent', color: primary ? J.onSky : J.text,
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {children}
  </ButtonBase>
);

const useDebounced = (value: string, ms = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return debounced;
};

const suggestionNote = (item: SubstituteProductItem): string => {
  if (item.kind === 'new') {
    const days = item.purchase_days_after ?? 0;
    return `nueva desde ${formatDayDate(item.first_purchase_after ?? null)} · ${formatInteger(days)} ${days === 1 ? 'día' : 'días'} con compra · ${formatEuro(item.amount_after ?? null, 0)}`;
  }
  if (item.kind === 'increased') {
    return item.units_change_pct != null
      ? `${formatPercent(item.units_change_pct, 0, { signed: true })} de unidades`
      : 'más unidades';
  }
  return '';
};

const CadenceManagementDrawer: React.FC<Props> = ({ open, target, onClose, onConfirm }) => {
  const manual = !target?.first_outside_p90_date;
  const [choice, setChoice] = useState<CadenceResolutionChoice>('cause');
  const [reason, setReason] = useState<string | null>(null);
  const [substitute, setSubstitute] = useState<SubstituteProductItem | null>(null);
  const [search, setSearch] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [conclusionEdited, setConclusionEdited] = useState(false);
  const [actionTaken, setActionTaken] = useState<string | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [nextAction, setNextAction] = useState<string | null>(null);
  const [otherAction, setOtherAction] = useState('');
  const [review, setReview] = useState<ReviewShortcut | null>(null);
  const [otherDate, setOtherDate] = useState('');
  const [note, setNote] = useState('');
  const [attempted, setAttempted] = useState(false);

  // Cada aviso empieza limpio.
  useEffect(() => {
    if (!open) return;
    setChoice(manual ? 'commercial' : 'cause');
    setReason(null); setSubstitute(null); setSearch(''); setConclusion(''); setConclusionEdited(false);
    setActionTaken(null); setActionNotes(''); setNextAction(null); setOtherAction('');
    setReview(null); setOtherDate(''); setNote(''); setAttempted(false);
  }, [open, target?.customer_id, target?.product_id, target?.first_outside_p90_date, manual]);

  const activityQuery = useQuery({
    queryKey: ['margin-center', 'cadence-management', 'activity', target?.customer_id, target?.product_id],
    queryFn: () => getMarginCenterRelationshipActivity12m([{ customer_id: target!.customer_id, product_id: target!.product_id }]),
    enabled: open && Boolean(target),
    staleTime: 60 * 1000,
  });
  const activity = activityQuery.data?.items?.[0] ?? null;
  const openFollowUp: CadenceOpenFollowUp | null = activity?.open_follow_up ?? null;

  const debouncedSearch = useDebounced(search.trim());
  const substitutesQuery = useQuery({
    queryKey: ['margin-center', 'cadence-management', 'substitutes', target?.customer_id, target?.product_id, target?.last_purchase_date, debouncedSearch],
    queryFn: () => getMarginCenterSubstituteProducts(target!.customer_id, {
      since: target!.last_purchase_date,
      excludeProductId: target!.product_id,
      q: debouncedSearch.length >= 2 ? debouncedSearch : null,
      // Sugeridas: unas pocas (boceto S2); el buscador, hasta 8.
      limit: debouncedSearch.length >= 2 ? 8 : 3,
    }),
    enabled: open && Boolean(target) && choice === 'cause' && reason === SUBSTITUTION_REASON
      && (debouncedSearch.length >= 2 || Boolean(target?.last_purchase_date)),
    staleTime: 60 * 1000,
  });
  const searching = debouncedSearch.length >= 2;

  // Conclusión generada a partir de la selección, mientras no se haya editado.
  useEffect(() => {
    if (!target || conclusionEdited || !reason) return;
    setConclusion(buildCauseConclusion(reason, target.product_id, substitute
      ? { product_id: substitute.product_id, product_label: substitute.product_label, since: substitute.first_purchase_after ?? null }
      : null));
  }, [reason, substitute, target, conclusionEdited]);

  const reviewDate = review === 'other' ? otherDate : review ? reviewDateFor(review) : null;
  const nextActionText = nextAction === OTHER_ACTION ? otherAction.trim() : nextAction;

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (choice === 'cause') {
      if (!reason) e.reason = 'Elige un motivo.';
      if (!conclusion.trim()) e.conclusion = 'Escribe la conclusión.';
    }
    if (choice === 'commercial') {
      if (!nextActionText) e.nextAction = nextAction === OTHER_ACTION ? 'Escribe la próxima acción.' : 'Elige la próxima acción.';
      if (!reviewDate) e.review = 'Elige la fecha de revisión.';
    }
    return e;
  }, [choice, reason, conclusion, nextAction, nextActionText, reviewDate]);

  if (!target) return null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (Object.keys(errors).length) return;
    if (choice === 'cause') {
      onConfirm(target, { kind: 'cause', reason: reason!, substituteProductId: reason === SUBSTITUTION_REASON ? substitute?.product_id ?? null : null, conclusion: conclusion.trim() });
    } else if (choice === 'commercial') {
      onConfirm(target, {
        kind: 'commercial',
        openingReason: buildOpeningReason(actionTaken, actionNotes),
        nextAction: nextActionText!,
        nextReviewIso: reviewDateToIso(reviewDate!),
        followUpId: openFollowUp?.id ?? null,
      });
    } else {
      onConfirm(target, { kind: 'not_relevant', note: note.trim() });
    }
  };

  const facts = target.last_purchase_date
    ? `Sin compra desde el ${formatDayDate(target.last_purchase_date)}${target.days_since_last_purchase != null ? ` · ${formatInteger(target.days_since_last_purchase)} días` : ''}${target.p90_interval_days != null ? ` · frecuencia habitual ${usualFrequencyLabel(target.p90_interval_days)}` : ''}`
    : null;

  const resolutions = manual ? RESOLUTIONS.filter((r) => r.id === 'commercial') : RESOLUTIONS;
  const primaryLabel = choice === 'cause' ? 'Archivar' : choice === 'not_relevant' ? 'Descartar'
    : openFollowUp ? 'Añadir al seguimiento existente' : 'Abrir seguimiento';
  const error = (key: string) => attempted && errors[key]
    ? <Box sx={{ fontSize: 12, color: J.dangerText, mt: '6px', fontFamily: J.font }}>{errors[key]}</Box>
    : null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, bgcolor: J.panel, backgroundImage: 'none', borderLeft: '1px solid rgba(148,163,184,.16)' } }}>
      <Box component="form" onSubmit={submit} sx={{ p: '22px 24px', display: 'flex', flexDirection: 'column', minHeight: '100%', fontFamily: J.font, color: J.text }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: J.skyText }}>Gestión del aviso</Box>
            <Box component="h2" sx={{ fontSize: 18, fontWeight: 800, m: '4px 0' }}>
              {target.customer_label} · {target.product_id}{target.product_label && target.product_label !== target.product_id ? ` · ${target.product_label}` : ''}
            </Box>
          </Box>
          <IconButton aria-label="Cerrar" onClick={onClose} sx={{ width: 34, height: 34, border: '1px solid #334155', borderRadius: '10px', color: J.skyText }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        {facts && <Box sx={noteSx}>{facts}</Box>}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', mt: '12px' }}>
          {[
            ['Ventas 12 meses', activity ? formatEuro(sales12mValue(activity), 0) : '…'],
            ['Margen %', activity ? margin12mLabel(activity, (v) => formatPercent(v)) : '…'],
            ['Días con compra (12 meses)', activity ? formatInteger(activity.purchase_days_12m) : '…'],
          ].map(([k, v]) => (
            <Box key={k} sx={{ border: '1px solid #233149', borderRadius: '10px', p: '8px 10px' }}>
              <Box component="span" sx={{ fontSize: 11.5, color: J.muted, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k}</Box>
              <Box component="b" sx={{ fontSize: 14 }}>{v}</Box>
            </Box>
          ))}
        </Box>

        <Box sx={lblSx}>Resolución</Box>
        <Box role="radiogroup" aria-label="Resolución" sx={{ display: 'grid', gridTemplateColumns: `repeat(${resolutions.length === 1 ? 1 : 3}, 1fr)`, gap: '8px' }}>
          {resolutions.map((r) => {
            const on = choice === r.id;
            return (
              <ButtonBase
                key={r.id}
                role="radio"
                aria-checked={on}
                onClick={() => { setChoice(r.id); setAttempted(false); }}
                sx={{
                  border: '1px solid', borderColor: on ? J.sky : '#233149', borderRadius: '12px', p: '12px',
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px', textAlign: 'left',
                  bgcolor: on ? 'rgba(56,189,248,.08)' : 'transparent', fontFamily: J.font,
                }}
              >
                <Box component="b" sx={{ fontSize: 13.5, color: on ? J.skyText : J.text }}>{r.title}</Box>
                <Box component="span" sx={{ fontSize: 11.5, color: J.muted, lineHeight: 1.35 }}>{r.hint}</Box>
              </ButtonBase>
            );
          })}
        </Box>

        {choice === 'cause' && (
          <>
            <Box sx={lblSx}>Motivo</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {CAUSE_REASONS.map((r) => <Chip key={r} on={reason === r} onClick={() => setReason(r)}>{r}</Chip>)}
            </Box>
            {error('reason')}
            {reason === SUBSTITUTION_REASON && (
              <>
                <Box sx={lblSx}>Referencia sustitutiva <Box component="em" sx={optSx}>(opcional)</Box></Box>
                <InputBase
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por código o descripción"
                  startAdornment={<SearchRoundedIcon sx={{ fontSize: 18, color: '#64748b', mr: 1 }} />}
                  inputProps={{ 'aria-label': 'Buscar referencia sustitutiva' }}
                  sx={inputSx}
                />
                {!searching && target.last_purchase_date && (
                  <Box sx={{ ...noteSx, m: '8px 0 6px' }}>
                    Sugeridas: referencias que este cliente empezó a comprar o incrementó desde el {formatDayDate(target.last_purchase_date)}
                  </Box>
                )}
                <Box sx={{ mt: searching ? '8px' : 0 }}>
                  {(substitutesQuery.data?.items ?? []).map((item) => {
                    const on = substitute?.product_id === item.product_id;
                    return (
                      <ButtonBase
                        key={item.product_id}
                        onClick={() => setSubstitute(on ? null : item)}
                        aria-pressed={on}
                        sx={{
                          width: '100%', border: '1px solid', borderColor: on ? J.sky : '#233149', borderRadius: '10px',
                          p: '9px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
                          fontSize: 13, fontFamily: J.font, color: J.text, mb: '6px', textAlign: 'left',
                        }}
                      >
                        <span>{on ? '● ' : ''}{item.product_id}{item.product_label && item.product_label !== item.product_id ? ` · ${item.product_label}` : ''}</span>
                        <Box component="span" sx={noteSx}>{suggestionNote(item)}</Box>
                      </ButtonBase>
                    );
                  })}
                  {substitutesQuery.isFetched && !(substitutesQuery.data?.items ?? []).length && (
                    <Box sx={noteSx}>{searching ? 'No hay referencias con ese código o descripción.' : 'No hay referencias sugeridas para este cliente.'}</Box>
                  )}
                </Box>
              </>
            )}
            <Box sx={lblSx}>Conclusión <Box component="em" sx={optSx}>(generada a partir de la selección; editable)</Box></Box>
            <InputBase
              multiline
              minRows={2}
              value={conclusion}
              onChange={(e) => { setConclusion(e.target.value); setConclusionEdited(true); }}
              inputProps={{ 'aria-label': 'Conclusión', maxLength: 10000 }}
              sx={inputSx}
            />
            {error('conclusion')}
          </>
        )}

        {choice === 'commercial' && (
          <>
            <Box sx={lblSx}>Actuación realizada <Box component="em" sx={optSx}>(opcional)</Box></Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {ACTIONS_TAKEN.map((a) => <Chip key={a} on={actionTaken === a} onClick={() => setActionTaken(actionTaken === a ? null : a)}>{a}</Chip>)}
            </Box>
            <InputBase
              multiline
              minRows={1}
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              placeholder="Observaciones (opcional)"
              inputProps={{ 'aria-label': 'Observaciones', maxLength: 900 }}
              sx={{ ...inputSx, mt: '8px' }}
            />
            <Box sx={lblSx}>Próxima acción</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {NEXT_ACTIONS.map((a) => <Chip key={a} on={nextAction === a} onClick={() => setNextAction(a)}>{a}</Chip>)}
            </Box>
            {nextAction === OTHER_ACTION && (
              <InputBase
                autoFocus
                value={otherAction}
                onChange={(e) => setOtherAction(e.target.value)}
                placeholder="Describe la próxima acción"
                inputProps={{ 'aria-label': 'Otra acción', maxLength: 1000 }}
                sx={{ ...inputSx, mt: '8px' }}
              />
            )}
            {error('nextAction')}
            <Box sx={lblSx}>Fecha de revisión</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {REVIEW_SHORTCUTS.map((s) => {
                const on = review === s.id;
                const date = s.id !== 'other' ? reviewDateFor(s.id) : null;
                return (
                  <Chip key={s.id} on={on} onClick={() => setReview(s.id)}>
                    {on && date ? `${s.label} · ${formatDayDate(date)}` : s.label}
                  </Chip>
                );
              })}
            </Box>
            {review === 'other' && (
              <Box sx={{ mt: '10px', maxWidth: 260 }}>
                <ProaDatePicker label="Fecha de revisión" value={otherDate} onChange={setOtherDate} size="small" minDate={toDateOnly(new Date())} />
              </Box>
            )}
            {error('review')}
            {openFollowUp && (
              <Box sx={{ ...noteSx, mt: '12px' }}>
                Esta relación ya tiene un seguimiento abierto{openFollowUp.next_action ? ` («${openFollowUp.next_action}»)` : ''}: la gestión se añade a ese seguimiento.
              </Box>
            )}
          </>
        )}

        {choice === 'not_relevant' && (
          <>
            <Box sx={lblSx}>Nota <Box component="em" sx={optSx}>(opcional)</Box></Box>
            <InputBase
              multiline
              minRows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ 'aria-label': 'Nota', maxLength: 9000 }}
              sx={inputSx}
            />
          </>
        )}

        <Box sx={{ mt: 'auto', pt: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <FootButton onClick={onClose}>Cancelar</FootButton>
          {/* Hasta saber si la relación ya tiene un seguimiento abierto, no se guarda. */}
          <FootButton primary type="submit" disabled={activityQuery.isLoading}>{primaryLabel}</FootButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CadenceManagementDrawer;
